import React, { useContext, useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import SearchHashtags from "../../components/search/SearchHashtags";
import { Filter } from "./Filter";
import FeedAdvertisement from "../../components/feeds/sidebar/FeedAdvertisement";
import { useLocation } from "react-router-dom";
import { useInfiniteQuery } from "react-query";
import { getAllSearchAgainstKeywordApi } from "../../services/searchApis";
import { toast } from "react-toastify";
import { PostsCard } from "../../components/search/PostsResultsComp";
import PostCardShimmer from "../../components/feeds/cards/PostCardShimmer";
import InfiniteScroll from "react-infinite-scroller";
import { Language, LoginUserData } from "../../App";
import { useSplashAds } from "../../hooks/useSplashAds";
import SearchResultCard from "../../components/search/SearchResultCard";
import { LuFileText, LuFileQuestion, LuSearch } from "react-icons/lu";

export default function PostsSearch() {
  const query = useLocation().search;
  const { lang } = useContext(Language);
  const keyword = decodeURIComponent(query.split("=")[1] || "");
  const { splashData } = useContext(LoginUserData);
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const [date, setDate] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);

  const specialization = selectedSpecializations;
  const sort = sortBy;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["search-posts", keyword],
      queryFn: ({ pageParam = 1 }) =>
        getAllSearchAgainstKeywordApi({
          keyword,
          type: "posts",
          pageParam,
          specialization,
          date,
          sort,
        }),
      enabled: !!keyword,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const posts = data?.pages?.flatMap((page) => page?.data?.data) || [];
  const leftAds = splashAds?.search_left || [];
  const rightAds = splashAds?.search_right || [];
  const hashTags = data?.pages?.flatMap((page) => page?.hash_tags);
  const storedValue = JSON.parse(localStorage.getItem("contest_obj"));

  useEffect(() => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  }, [selectedSpecializations, date, sortBy, refetch]);

  return (
    <div className="search-page-wrapper">
      <NavBar />
      <Filter
        title={lang?.posts || "Posts"}
        options={[
          { title: lang?.sort_by || "Sort By", options: [lang?.newest_to_oldest || "Newest to Oldest", lang?.oldest_to_newest || "Oldest to Newest"] },
          { title: lang?.date || "Date", options: [] },
        ]}
        onSelectDate={(date) => setDate(date)}
        onSortBy={(sort) => setSortBy(sort)}
        onSpecializationSelect={(specializations) =>
          setSelectedSpecializations(specializations ? specializations.map((spec) => spec.value) : [])
        }
      />

      <div className="search-layout-container">
        {/* Left Column: Ads */}
        <div className="search-left-col">
          <FeedAdvertisement
            ads={leftAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>

        {/* Center Column: Results */}
        <div className="search-main-col">
          <div className="search-results-header-inline">
            <h1 className="search-main-heading">
              <span>{lang?.posts || "Posts"}</span>
              {keyword && (
                <span className="search-keyword-badge">
                  <LuSearch size={14} />
                  <span>"{keyword}"</span>
                </span>
              )}
            </h1>
            {!isLoading && !isRefetching && posts?.length > 0 && (
              <span className="search-results-summary">
                {posts.length} {posts.length === 1 ? (lang?.result || "result") : (lang?.results || "results")} {lang?.found || "found"}
              </span>
            )}
          </div>

          <PostsList
            loading={isLoading || isRefetching}
            data={posts}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        {/* Right Column: Hashtags & Ads */}
        <div className="search-right-col">
          <SearchHashtags data={hashTags} />
          <FeedAdvertisement
            ads={rightAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>
      </div>
    </div>
  );
}

const PostsList = ({
  loading,
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const { lang } = useContext(Language);

  return (
    <SearchResultCard
      icon={<LuFileText />}
      title={lang?.posts || "Posts"}
      count={data?.length}
      seeAll={false}
      showHeader={false}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (!isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={hasNextPage}
        loader={<PostCardShimmer count={1} />}
      >
        {loading ? (
          <PostCardShimmer count={3} />
        ) : data && data.length > 0 ? (
          <div className="search-posts-list">
            {data.map((post, index) => (
              <PostsCard key={post?.id || index} post={post} />
            ))}
          </div>
        ) : (
          <div className="search-empty-state-card">
            <div className="search-empty-icon-wrap">
              <LuFileQuestion />
            </div>
            <h5 className="search-empty-title">{lang?.no_posts_found || "No posts found"}</h5>
            <p className="search-empty-desc">
              {lang?.try_different_keyword || "Try a different keyword or check spelling."}
            </p>
          </div>
        )}
      </InfiniteScroll>
    </SearchResultCard>
  );
};
